import type { AgentPayoutOutcome, AgentPayoutPayload } from "@/shared/realtime";
import { formatXof } from "@/shared/format";

/**
 * Gains et retraits d'une Main — document du 2026-09-05.
 *
 * Module PUR → testable sans mock.
 *
 * ## Ce qu'une Main gagne, et ce qu'elle ne gagne pas
 * Le gain d'une course, ce sont ses **frais de distance**, moins un **forfait
 * fixe** retenu par la plateforme. Le reste des frais de service — la base par
 * arrêt, l'urgence, l'heure de pointe — ne lui revient pas : il couvre les
 * commissions de l'agrégateur et les versements aux boutiques.
 *
 * Sur une course de 6,5 km, deux arrêts, en heure de pointe :
 *
 *   | Base 2 arrêts        |  200 F  → plateforme |
 *   | Distance 6,5 km      |  650 F  → la Main    |
 *   | Heure de pointe      |  100 F  → plateforme |
 *   | Payé par le client   |  950 F               |
 *   | Retenue              | −100 F               |
 *   | **Gain de la Main**  | **550 F**            |
 *
 * Retenir l'ordre de grandeur : sur 950 F encaissés, la Main en garde 550. Et
 * sur une course d'épicerie où le client paie 20 000 F de marchandise, elle en
 * garde toujours 550 — la marchandise n'est pas un revenu, c'est une avance.
 * Confondre les deux sur un écran de gains est la faute la plus coûteuse en
 * confiance qu'on puisse commettre ici.
 */

// ─── Le gain d'une course ────────────────────────────────────────────────────

/**
 * Statut d'un gain, tel que le serveur le renvoie sur chaque ligne.
 *
 * - `ACQUIS` : disponible au retrait.
 * - `PAYE`   : déjà emporté par un retrait soldé.
 */
export type EarningStatus = "ACQUIS" | "PAYE";

export function earningStatusLabel(statut: EarningStatus | string): string {
  if (statut === "ACQUIS") return "Acquis";
  if (statut === "PAYE") return "Retiré";
  // Un statut inconnu (serveur en avance sur l'app) se montre tel quel plutôt
  // que d'être masqué : mieux vaut un mot brut qu'une ligne qui ment.
  return statut;
}

/**
 * Le gain d'une course est-il tombé à zéro faute de distance ?
 *
 * **Le gain n'est jamais négatif.** Sur une course trop courte pour couvrir la
 * retenue, la Main gagne zéro — *elle ne doit rien*. Cette nuance n'est pas
 * théorique : on affiche le calcul « frais de distance − retenue » sur chaque
 * ligne (c'est ce qui rend un gain contestable, donc acceptable), et cette
 * soustraction-là, elle, PEUT être négative. Une Main qui lit « 80 − 100 » sans
 * un mot de plus en conclut qu'elle doit 20 F à la plateforme.
 *
 * Le montant fait toujours foi : c'est le serveur qui l'a plafonné à zéro.
 */
export function isGainZeroedByRetenue(fraisDistance: number, retenue: number): boolean {
  return fraisDistance - retenue <= 0;
}

/**
 * Ce qu'on écrit sous une ligne dont le gain a été ramené à zéro.
 *
 * Le point important est la seconde phrase — sans elle, la première se lit
 * comme une dette.
 */
export const GAIN_ZERO_NOTICE =
  "Course trop courte pour couvrir la retenue : le gain est de zéro. Vous ne devez rien.";

/**
 * Le gain est acquis au RÈGLEMENT de la course, pas à la livraison.
 *
 * Entre les deux, la Main a fini son travail et son gain n'apparaît pas encore.
 * C'est normal, et l'écran ne doit pas le présenter comme une anomalie — d'où
 * cette phrase, posée en permanence sur l'écran des gains plutôt qu'en alerte.
 */
export const SETTLEMENT_DELAY_NOTICE =
  "Le gain d'une course apparaît à son règlement, pas à la livraison. Une course tout juste livrée peut ne pas encore figurer ici.";

// ─── Demander un retrait ─────────────────────────────────────────────────────

/**
 * Ce que la Main recevra vraiment, une fois les frais de l'opérateur déduits.
 *
 * **Les frais sont à sa charge.** Le lui apprendre après le virement, sur son
 * propre argent, est la meilleure façon de générer un appel au support — d'où
 * l'obligation d'afficher les trois lignes (demandé / frais / reçu) AVANT
 * qu'elle valide.
 *
 * ⚠️ Ce calcul sert à ANNONCER, jamais à confirmer. Une fois la demande
 * enregistrée, c'est le `net_amount` de la réponse `201` qu'on affiche : lui
 * seul est le chiffre du serveur.
 */
export function netPayoutAmount(amount: number, withdrawalFee: number): number {
  // Jamais sous zéro : un montant inférieur aux frais donnerait un « vous
  // recevrez −50 F » absurde. Le minimum de retrait rend le cas improbable,
  // mais les deux valeurs viennent du serveur et peuvent changer.
  return Math.max(0, amount - withdrawalFee);
}

export type PayoutAmountVerdict =
  | { ok: true }
  | { ok: false; reason: "vide" | "sous_le_minimum" | "au_dela_du_solde"; message: string };

export type PayoutAmountInput = {
  amount: number | null;
  /** `minimum_de_retrait` — vient du serveur, JAMAIS codé en dur. */
  minimum: number;
  /** `solde_disponible` — le seul montant retirable, tel que rendu. */
  available: number;
};

/**
 * Le montant demandé est-il recevable, avant même d'appeler le serveur ?
 *
 * ## Pourquoi bloquer côté écran
 * Le document le demande explicitement pour le minimum : « à bloquer **avant
 * l'envoi**, avec `minimum_de_retrait` ». Envoyer une demande qu'on sait
 * refusée fait payer à la Main un aller-retour réseau pour apprendre une règle
 * qu'on connaissait déjà.
 *
 * ## Pourquoi les textes sont les nôtres ici, et pourquoi c'est légitime
 * Ailleurs dans l'app, la règle est absolue : on affiche le message du serveur,
 * jamais le nôtre. Ici il n'y a pas de réponse serveur — on l'a évitée. Ces
 * phrases reprennent donc la formulation du serveur au plus près, pour qu'une
 * Main qui déclenche les deux chemins ne lise pas deux règles différentes pour
 * la même contrainte.
 *
 * ## Le solde, lui, reste l'affaire du serveur
 * On refuse un montant qui dépasse le solde AFFICHÉ, par simple cohérence :
 * proposer un chiffre comme « le seul montant retirable » puis laisser saisir
 * au-delà serait incohérent. Mais ce garde-fou n'est pas une vérité — le solde
 * a pu bouger. Si le serveur répond « Gains insuffisants », c'est LUI qui a
 * raison : on affiche son message et on recharge `GET /agent-earnings`.
 */
export function checkPayoutAmount({
  amount,
  minimum,
  available,
}: PayoutAmountInput): PayoutAmountVerdict {
  if (amount == null || !Number.isFinite(amount) || amount <= 0) {
    return { ok: false, reason: "vide", message: "Indiquez le montant à retirer." };
  }
  if (amount < minimum) {
    return {
      ok: false,
      reason: "sous_le_minimum",
      message: `Le retrait minimum est de ${formatXof(minimum)}.`,
    };
  }
  if (amount > available) {
    return {
      ok: false,
      reason: "au_dela_du_solde",
      message: `Vous ne pouvez pas retirer plus que votre solde disponible (${formatXof(available)}).`,
    };
  }
  return { ok: true };
}

// ─── L'historique des retraits ───────────────────────────────────────────────

export type PayoutStatus = "EN_ATTENTE" | "EN_COURS" | "PAYE" | "REFUSE" | "ECHOUE";

export type PayoutStatusView = {
  /** Ce que la Main doit lire. */
  label: string;
  /** Ce qu'elle peut faire — vide quand il n'y a rien à faire. */
  action: string;
  /** Peut-elle refaire une demande ? */
  canRetry: boolean;
  /** Le virement est-il en cours de traitement (couleur d'attente) ? */
  pending: boolean;
  /** L'issue est-elle négative (couleur d'alerte) ? */
  failed: boolean;
};

/**
 * Les cinq états d'une demande, avec les libellés du document.
 *
 * Le tableau du document donne mot pour mot ce que la Main doit lire et ce
 * qu'elle peut faire. On ne s'en écarte pas : ces phrases ont été écrites pour
 * qu'une Main sache si elle doit patienter ou agir, et toute reformulation
 * rouvre exactement la question qu'elles ferment.
 */
export function payoutStatusView(status: PayoutStatus | string): PayoutStatusView {
  switch (status) {
    case "EN_ATTENTE":
      return {
        label: "En attente d'approbation",
        action: "Rien à faire, patientez.",
        canRetry: false,
        pending: true,
        failed: false,
      };
    case "EN_COURS":
      return {
        label: "Virement en cours",
        action: "Rien à faire, patientez.",
        canRetry: false,
        pending: true,
        failed: false,
      };
    case "PAYE":
      return { label: "Reçu", action: "", canRetry: false, pending: false, failed: false };
    case "REFUSE":
      return {
        label: "Refusé",
        action: "Vous pouvez redemander.",
        canRetry: true,
        pending: false,
        failed: true,
      };
    case "ECHOUE":
      return {
        label: "Le virement n'a pas abouti",
        action: "Vous pouvez redemander.",
        canRetry: true,
        pending: false,
        failed: true,
      };
    default:
      // Statut inconnu : on le montre brut plutôt que de le ranger d'office
      // dans « en attente », ce qui ferait patienter une Main pour rien.
      return { label: status, action: "", canRetry: false, pending: false, failed: false };
  }
}

/**
 * La phrase qui évite l'appel au support.
 *
 * ⚠️ Le document est catégorique : « **Insistez sur ceci à l'écran : un refus
 * comme un échec ne fait perdre aucun gain.** Ils redeviennent disponibles
 * immédiatement, et le solde le montre. Une main qui croit avoir perdu son
 * argent appelle, et elle a raison d'appeler si rien ne le lui dit. »
 *
 * Ce n'est pas un message d'erreur inventé — l'app n'a pas le droit d'en
 * écrire. C'est une garantie que le document ORDONNE d'afficher, et dont le
 * serveur n'envoie l'équivalent (`consequence`) que par le temps réel, donc pas
 * sur cet écran. Quand `consequence` est là, c'est LUI qu'on montre.
 */
export const PAYOUT_NO_LOSS_NOTICE =
  "Un refus comme un échec ne vous fait perdre aucun gain : ils redeviennent disponibles immédiatement, et votre solde le montre.";

/**
 * Le motif d'une issue négative, pris là où le serveur l'a mis.
 *
 * Deux champs selon la cause — `resolution_note` quand un opérateur a tranché,
 * `payout_error` quand le virement lui-même a échoué. L'un ou l'autre, jamais
 * les deux, et rien à écrire quand ni l'un ni l'autre n'est renseigné : une
 * phrase de remplacement inventée ici serait un message métier fabriqué.
 */
export function payoutFailureReason(payout: {
  status?: PayoutStatus | string;
  resolution_note?: string | null;
  payout_error?: string | null;
}): string | null {
  // Le STATUT décide quel champ fait foi, et non l'ordre de présence : le
  // document attache `resolution_note` à REFUSE et `payout_error` à ECHOUE. Si
  // les deux étaient renseignés — une demande refusée puis rejouée, par
  // exemple — lire le premier trouvé afficherait le motif de l'autre issue.
  const preferred =
    payout.status === "ECHOUE"
      ? (payout.payout_error ?? payout.resolution_note)
      : payout.status === "REFUSE"
        ? (payout.resolution_note ?? payout.payout_error)
        : (payout.resolution_note ?? payout.payout_error);
  const trimmed = preferred?.trim();
  return trimmed ? trimmed : null;
}

/**
 * Ce qu'on dit à la Main quand une issue de retrait arrive en direct.
 *
 * Sur un échec ou un refus : le MOTIF puis la CONSÉQUENCE. Le motif explique,
 * la conséquence rassure — et c'est la conséquence que le document ordonne
 * d'afficher telle quelle, parce qu'elle seule empêche la Main de conclure
 * qu'elle a perdu son argent. Notre garantie générale ne sert que si le serveur
 * n'a rien écrit.
 *
 * Sur un versement : on n'annonce QUE `net_amount`. Annoncer le montant demandé
 * à sa place surestimerait le virement des frais, exactement au moment où elle
 * vérifie son compte.
 */
export function payoutNoticeMessage(
  outcome: AgentPayoutOutcome,
  payload: AgentPayoutPayload,
): string {
  if (outcome === "paid") {
    const sur = payload.phone ? ` sur ${payload.phone}` : "";
    return typeof payload.net_amount === "number"
      ? `Retrait reçu : ${formatXof(payload.net_amount)}${sur}`
      : `Votre retrait a été versé${sur}.`;
  }

  const motif = payload.motif?.trim() || payload.raison?.trim() || null;
  const consequence = payload.consequence?.trim() || PAYOUT_NO_LOSS_NOTICE;
  return motif ? `${motif} ${consequence}` : consequence;
}

/**
 * Le dernier numéro utilisé par la Main, pour le pré-remplissage.
 *
 * ## Pourquoi il faut le déduire
 * Le schéma `Agent` ne porte AUCUN téléphone, et aucune route ne rend « le
 * dernier numéro utilisé ». Le serveur, lui, le connaît — c'est pourquoi
 * `phone` devient facultatif après la première demande. Mais pour PROPOSER ce
 * numéro pré-rempli et modifiable, comme le document le demande, l'app doit le
 * retrouver quelque part : l'historique des retraits est le seul endroit.
 *
 * Tri par date décroissante quand elle est là. Quand elle manque partout, le
 * tri est stable et le premier élément l'emporte — c'est-à-dire l'ordre du
 * serveur, qui rend le plus récent en tête.
 */
export function lastPayoutPhone(
  payouts: ReadonlyArray<{ phone?: string | null; created_at?: string | null }>,
): string | null {
  const withPhone = payouts.filter((p) => typeof p.phone === "string" && p.phone.trim() !== "");
  if (withPhone.length === 0) return null;

  const parse = (d?: string | null) => (d ? Date.parse(d) : Number.NaN);
  const sorted = [...withPhone].sort((a, b) => {
    const ta = parse(a.created_at);
    const tb = parse(b.created_at);
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
    if (Number.isNaN(ta)) return 1;
    if (Number.isNaN(tb)) return -1;
    return tb - ta;
  });
  return sorted[0].phone!.trim();
}

/**
 * Compare sans accents ni casse.
 *
 * Les messages relevés ne sont pas systématiquement accentués — le document
 * écrit « Indiquez le numero qui doit recevoir l'argent. » Faire dépendre le
 * comportement d'un accent serait absurde. Même précaution que sur les refus de
 * provision (`features/trips/lib/provisionComplement`).
 */
function normalize(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/**
 * Ce refus dit-il que les gains ne suffisent plus ?
 *
 * « Gains insuffisants : 320 XOF disponibles. »
 *
 * ⚠️ Sert à décider s'il faut RECHARGER `GET /agent-earnings`, jamais à écrire
 * un message : le solde a bougé depuis l'affichage, et le montrer périmé à côté
 * d'un refus est le meilleur moyen de faire douter la Main des deux chiffres.
 *
 * À ne pas confondre avec le « Solde insuffisant » du portefeuille CLIENT, qui
 * parle d'un tout autre compte.
 */
export function mentionsInsufficientEarnings(message: string | null | undefined): boolean {
  if (!message) return false;
  return normalize(message).includes("gains insuffisants");
}

/**
 * Ce refus réclame-t-il le numéro qui doit recevoir l'argent ?
 *
 * « Indiquez le numero qui doit recevoir l'argent. » — c'est le cas de la
 * PREMIÈRE demande, où la plupart des Mains n'ont aucun numéro enregistré.
 * Sert à ouvrir le champ, pas à écrire le message.
 */
export function mentionsMissingPhone(message: string | null | undefined): boolean {
  if (!message) return false;
  const m = normalize(message);
  return m.includes("numero qui doit recevoir") || m.includes("indiquez le numero");
}
